"use client"

import { useMemo } from "react"
import { useRef, useEffect, useState } from "react"
import { Canvas } from "@react-three/fiber"
import { OrbitControls, useGLTF, Html, PerspectiveCamera } from "@react-three/drei"
import * as THREE from "three"
import { useTheme } from "./theme-provider"

// Map of brain activities to regions with vibrant colors
const BRAIN_REGIONS = {
  frontalLobe: {
    name: "Frontal Lobe",
    position: [-1.2, 1.1, 0.8],
    color: "#FF5E5B", // Vibrant Red
    activities: [
      "decision making",
      "planning",
      "problem solving",
      "judgment",
      "impulse control",
      "emotional expression",
      "movement",
      "speech",
      "attention",
      "motivation",
      "executive function",
      "personality",
    ],
    meshNames: ["frontal", "prefrontal"],
  },
  parietalLobe: {
    name: "Parietal Lobe",
    position: [0.5, 1.4, 0.7],
    color: "#39D98A", // Vibrant Green
    activities: [
      "perception",
      "spatial awareness",
      "touch",
      "navigation",
      "sensation",
      "reading",
      "pain",
      "temperature",
      "pressure",
      "body awareness",
      "calculation",
      "spatial reasoning",
    ],
    meshNames: ["parietal"],
  },
  temporalLobe: {
    name: "Temporal Lobe",
    position: [1, 0.6, 1.3],
    color: "#6B66FF", // Vibrant Purple
    activities: [
      "memory",
      "language",
      "hearing",
      "emotion",
      "speech comprehension",
      "auditory processing",
      "face recognition",
      "object recognition",
      "long-term memory",
      "understanding",
      "naming objects",
    ],
    meshNames: ["temporal"],
  },
  occipitalLobe: {
    name: "Occipital Lobe",
    position: [-0.3, -0.5, 1.5],
    color: "#FFCC00", // Vibrant Yellow
    activities: [
      "vision",
      "visual processing",
      "color recognition",
      "motion perception",
      "visual interpretation",
      "depth perception",
      "visual memory",
      "reading",
    ],
    meshNames: ["occipital"],
  },
  cerebellum: {
    name: "Cerebellum",
    position: [-1.3, 0.4, 1.2],
    color: "#FF9E2A", // Vibrant Orange
    activities: [
      "balance",
      "coordination",
      "motor control",
      "posture",
      "timing",
      "fine movement",
      "muscle coordination",
      "equilibrium",
      "motor learning",
    ],
    meshNames: ["cerebellum"],
  },
  brainstem: {
    name: "Brainstem",
    position: [0, -0.2, 0],
    color: "#00C2FF", // Vibrant Cyan
    activities: [
      "breathing",
      "heart rate",
      "blood pressure",
      "swallowing",
      "consciousness",
      "sleep",
      "digestion",
      "sneezing",
      "coughing",
      "vomiting",
      "basic reflexes",
      "arousal",
      "alertness",
    ],
    meshNames: ["brainstem", "stem"],
  },
  hippocampus: {
    name: "Hippocampus",
    position: [0.5, 0, 0.5],
    color: "#FF66C4", // Vibrant Pink
    activities: ["memory formation", "spatial memory", "learning", "emotion", "navigation", "memory consolidation"],
    meshNames: ["hippocampus"],
  },
  amygdala: {
    name: "Amygdala",
    position: [0.7, 0.2, 0.7],
    color: "#FF3D7F", // Vibrant Magenta
    activities: [
      "fear",
      "anxiety",
      "aggression",
      "emotional processing",
      "emotional memory",
      "stress response",
      "threat detection",
    ],
    meshNames: ["amygdala"],
  },
  // Additional regions for specific areas mentioned in AI output
  brocasArea: {
    name: "Broca's Area",
    position: [-1.5, 0.8, 1.0],
    color: "#FF5757", // Bright Red
    activities: ["speech production", "language processing", "articulation"],
    meshNames: ["frontal"],
  },
  wernickesArea: {
    name: "Wernicke's Area",
    position: [1.2, 0.3, 1.0],
    color: "#8A4FFF", // Bright Purple
    activities: ["language comprehension", "speech understanding"],
    meshNames: ["temporal"],
  },
  superiorTemporalGyrus: {
    name: "Superior Temporal Gyrus",
    position: [1.3, 0.7, 0.9],
    color: "#7B61FF", // Bright Violet
    activities: ["auditory processing", "language processing"],
    meshNames: ["temporal"],
  },
  inferiorFrontalGyrus: {
    name: "Inferior Frontal Gyrus",
    position: [-1.4, 0.6, 1.2],
    color: "#FF4F4F", // Bright Red-Orange
    activities: ["language processing", "speech production"],
    meshNames: ["frontal"],
  },
}

// Custom positions for AI-specific brain regions
const AI_REGION_POSITIONS = {
  "Broca's Area": [-1.5, 0.8, 1.0],
  "Wernicke's Area": [1.2, 0.3, 1.0],
  "Superior Temporal Gyrus": [1.3, 0.7, 0.9],
  "Inferior Frontal Gyrus": [-1.4, 0.6, 1.2],
  Amygdala: [0.7, 0.2, 0.7],
  "Prefrontal Cortex": [-1.0, 1.3, 0.9],
  Hippocampus: [0.5, 0, 0.5],
  "Anterior Cingulate Cortex": [0, 0.8, 0.5],
  Hypothalamus: [0, 0.1, 0.3],
  "Limbic System": [0.3, 0.3, 0.6],
}

// Brain region label component with enhanced styling
const BrainLabels = ({ activeRegions, aiRegionLabels = [], moodTransition, theme }) => {
  // If we have AI region labels, use those instead of the active regions
  if (aiRegionLabels && aiRegionLabels.length > 0) {
    return aiRegionLabels.map((regionName, index) => {
      // Get position from custom mapping or use default
      const position =
        AI_REGION_POSITIONS[regionName] ||
        (BRAIN_REGIONS[regionName.toLowerCase().replace(/\s+/g, "")] || BRAIN_REGIONS.frontalLobe).position

      // Check if this region has a change in the mood transition
      let changeDirection = null
      if (moodTransition && moodTransition.regions_activation) {
        const regionChange = moodTransition.regions_activation.find((r) => r.region === regionName)
        if (regionChange) {
          changeDirection = regionChange.change
        }
      }

      return (
        <Html key={index} position={position} center>
          <div
            className="activity-label"
            style={{
              borderColor:
                changeDirection === "increasing" ? "#28a745" : changeDirection === "decreasing" ? "#dc3545" : "#ff0000",
              fontSize: "1rem", // Larger font size
              padding: "0.6rem 1.2rem", // Larger padding
              maxWidth: "180px", // Wider label
              backgroundColor: theme === "dark" ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.95)", // Theme-aware background
              color: theme === "dark" ? "#e0e0e0" : "#333", // Theme-aware text color
              boxShadow: "0 3px 10px rgba(0, 0, 0, 0.2)", // Stronger shadow
            }}
          >
            {regionName}
            {changeDirection && (
              <span
                style={{
                  marginLeft: "5px",
                  color: changeDirection === "increasing" ? "#28a745" : "#dc3545",
                  fontWeight: "bold",
                  fontSize: "1.1rem", // Larger arrow
                }}
              >
                {changeDirection === "increasing" ? "↑" : "↓"}
              </span>
            )}
          </div>
        </Html>
      )
    })
  }

  // Fallback to original behavior
  if (!activeRegions || activeRegions.length === 0) return null

  return activeRegions.map((region, index) => (
    <Html key={index} position={region.position} center>
      <div
        className="activity-label"
        style={{
          fontSize: "1rem", // Larger font size
          padding: "0.6rem 1.2rem", // Larger padding
          maxWidth: "180px", // Wider label
          backgroundColor: theme === "dark" ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.95)", // Theme-aware background
          color: theme === "dark" ? "#e0e0e0" : "#333", // Theme-aware text color
          boxShadow: "0 3px 10px rgba(0, 0, 0, 0.2)", // Stronger shadow
        }}
      >
        {region.name}
      </div>
    </Html>
  ))
}

// Mood transition visualization component
function MoodTransitionPanel({ moodTransition, theme }) {
  if (!moodTransition) return null

  return (
    <Html position={[0, 2.5, 0]} center>
      <div
        className="mood-transition"
        style={{
          backgroundColor: theme === "dark" ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.95)",
          color: theme === "dark" ? "#e0e0e0" : "#333",
        }}
      >
        <div className="mood-transition-title">Mood Transition</div>
        <div className="mood-transition-arrow">
          <span className="mood-from">{moodTransition.from.toUpperCase()}</span>
          <span className="mood-arrow">→</span>
          <span className="mood-to">{moodTransition.to.toUpperCase()}</span>
        </div>
        <div style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
          {moodTransition.regions_activation &&
            moodTransition.regions_activation.map((region, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: region.change === "increasing" ? "#28a745" : "#dc3545",
                }}
              >
                <span>{region.region}</span>
                <span>{region.change === "increasing" ? "↑" : "↓"}</span>
              </div>
            ))}
        </div>
      </div>
    </Html>
  )
}

const BrainModel = ({ activities, brainRegionLabels = [], moodTransition, theme }) => {
  const brainRef = useRef()
  const { scene } = useGLTF("./brain_project/scene.gltf")
  const [activeRegions, setActiveRegions] = useState([])

  // Create materials with theme-aware properties
  const defaultMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color(theme === "dark" ? "#555555" : "#D3D3D3"),
      roughness: 0.5,
      metalness: 0.2,
      transparent: true,
      opacity: 0.9,
      emissive: new THREE.Color(theme === "dark" ? "#222222" : "#000000"),
      emissiveIntensity: theme === "dark" ? 0.1 : 0,
    })
  }, [theme])

  const activeMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color("#ff0000"),
      roughness: 0.3,
      metalness: 0.3,
      emissive: new THREE.Color("#ff3333"),
      emissiveIntensity: 0.5,
    })
  }, [])

  // Create materials for increasing and decreasing activity (for mood transitions)
  const increasingMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color("#28a745"),
      roughness: 0.3,
      metalness: 0.3,
      emissive: new THREE.Color("#28a745"),
      emissiveIntensity: 0.5,
    })
  }, [])

  const decreasingMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color("#dc3545"),
      roughness: 0.3,
      metalness: 0.3,
      emissive: new THREE.Color("#dc3545"),
      emissiveIntensity: 0.5,
    })
  }, [])

  const regionMaterials = useMemo(() => {
    const materials = {}
    Object.entries(BRAIN_REGIONS).forEach(([key, region]) => {
      materials[key] = new THREE.MeshStandardMaterial({
        color: new THREE.Color(region.color),
        roughness: 0.4,
        metalness: 0.3,
        transparent: true,
        opacity: 0.95,
        emissive: new THREE.Color(region.color),
        emissiveIntensity: 0.2, // Add some glow to make colors pop
      })
    })
    return materials
  }, [])

  // Clone the scene and set up initial materials
  const clonedScene = useMemo(() => {
    const sceneClone = scene.clone(true)

    // First set all to default material
    sceneClone.traverse((child) => {
      if (child.isMesh) {
        child.material = defaultMaterial.clone()
        // Enable shadows for better visual quality
        child.castShadow = true
        child.receiveShadow = true
      }
    })

    // Then apply region-specific materials where appropriate
    Object.entries(BRAIN_REGIONS).forEach(([key, region]) => {
      sceneClone.traverse((child) => {
        if (child.isMesh) {
          const lowerName = child.name.toLowerCase()
          const belongsToRegion = region.meshNames.some((meshName) => lowerName.includes(meshName.toLowerCase()))

          if (belongsToRegion) {
            child.material = regionMaterials[key].clone()
            child.userData.region = key // Store region info for easy lookup
          }
        }
      })
    })

    return sceneClone
  }, [scene, defaultMaterial, regionMaterials])

  // Highlight active regions
  useEffect(() => {
    if (!clonedScene) return

    const matches = []
    const activityLower = activities.map((a) => a.toLowerCase())

    // Find matching regions
    Object.entries(BRAIN_REGIONS).forEach(([key, region]) => {
      const isActive = activityLower.some(
        (activity) =>
          region.activities.some(
            (regionActivity) =>
              activity.includes(regionActivity.toLowerCase()) || regionActivity.toLowerCase().includes(activity),
          ) || region.name.toLowerCase().includes(activity),
      )

      if (isActive) {
        matches.push(region)
      }
    })

    // Special handling for AI-specific regions
    if (brainRegionLabels && brainRegionLabels.length > 0) {
      brainRegionLabels.forEach((label) => {
        const regionKey = label.toLowerCase().replace(/\s+/g, "")
        const region =
          BRAIN_REGIONS[regionKey] ||
          (label.includes("Temporal")
            ? BRAIN_REGIONS.temporalLobe
            : label.includes("Frontal")
              ? BRAIN_REGIONS.frontalLobe
              : null)

        if (region && !matches.includes(region)) {
          matches.push(region)
        }
      })
    }

    setActiveRegions(matches)

    // Reset all materials first
    clonedScene.traverse((child) => {
      if (child.isMesh) {
        if (child.userData.region) {
          // Reset to region-specific material
          child.material = regionMaterials[child.userData.region].clone()
        } else {
          // Reset to default material
          child.material = defaultMaterial.clone()
        }
      }
    })

    // Highlight active regions
    matches.forEach((region) => {
      clonedScene.traverse((child) => {
        if (child.isMesh && child.userData.region === region.name.toLowerCase().replace(/\s+/g, "")) {
          // Check if this region has a change in the mood transition
          if (moodTransition && moodTransition.regions_activation) {
            const regionChange = moodTransition.regions_activation.find(
              (r) => r.region.toLowerCase().replace(/\s+/g, "") === region.name.toLowerCase().replace(/\s+/g, ""),
            )

            if (regionChange) {
              // Apply increasing or decreasing material based on the change
              child.material =
                regionChange.change === "increasing" ? increasingMaterial.clone() : decreasingMaterial.clone()
            } else {
              child.material = activeMaterial.clone()
            }
          } else {
            child.material = activeMaterial.clone()
          }
        }
      })
    })
  }, [
    activities,
    brainRegionLabels,
    clonedScene,
    activeMaterial,
    defaultMaterial,
    regionMaterials,
    moodTransition,
    increasingMaterial,
    decreasingMaterial,
  ])

  return (
    <>
      <primitive ref={brainRef} object={clonedScene} scale={2.5} position={[0, -1, 0]} />
      <BrainLabels
        activeRegions={activeRegions}
        aiRegionLabels={brainRegionLabels}
        moodTransition={moodTransition}
        theme={theme}
      />
      {moodTransition && <MoodTransitionPanel moodTransition={moodTransition} theme={theme} />}
    </>
  )
}

const BrainCanvas = ({ activities = [], brainRegionLabels = [], moodTransition }) => {
  const { theme } = useTheme()

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <Canvas className="brain-canvas" shadows>
        <PerspectiveCamera makeDefault position={[-4, 3, 6]} fov={45} />
        <color attach="background" args={[theme === "dark" ? "#1a1a2e" : "#f8f9fa"]} />

        {/* Enhanced Lighting for better visibility of colored parts */}
        <ambientLight intensity={theme === "dark" ? 0.6 : 0.8} />
        <directionalLight
          position={[10, 10, 10]}
          intensity={theme === "dark" ? 1.2 : 1.4}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <pointLight position={[-10, -10, -10]} intensity={theme === "dark" ? 0.7 : 0.9} />
        <spotLight position={[5, 5, 5]} angle={0.3} penumbra={1} intensity={theme === "dark" ? 1.2 : 1.4} castShadow />

        {/* Brain model with different colors for each region */}
        <BrainModel
          activities={activities}
          brainRegionLabels={brainRegionLabels}
          moodTransition={moodTransition}
          theme={theme}
        />

        {/* Controls */}
        <OrbitControls
          enableZoom={true}
          enablePan={true}
          enableRotate={true}
          autoRotate={false}
          autoRotateSpeed={0.5}
          maxPolarAngle={Math.PI / 1.5}
          minPolarAngle={Math.PI / 6}
        />
      </Canvas>
      
      {/* Brain Orientation Labels */}
      <div style={{
        position: "absolute",
        top: "10px",
        left: "20px",
        background: "rgba(0,0,0,0.7)",
        color: "#fff",
        padding: "8px 16px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: "600",
        pointerEvents: "none",
        zIndex: 10
      }}>
        🧠 Front (Frontal)
      </div>
      
      <div style={{
        position: "absolute",
        top: "10px",
        right: "20px",
        background: "rgba(0,0,0,0.7)",
        color: "#fff",
        padding: "8px 16px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: "600",
        pointerEvents: "none",
        zIndex: 10
      }}>
        👁️ Back (Occipital)
      </div>
      
      <div style={{
        position: "absolute",
        bottom: "10px",
        left: "50%",
        transform: "translateX(-50%)",
        background: "rgba(0,0,0,0.7)",
        color: "#fff",
        padding: "6px 12px",
        borderRadius: "15px",
        fontSize: "11px",
        fontWeight: "500",
        pointerEvents: "none",
        zIndex: 10,
        opacity: 0.8
      }}>
        💡 Drag to rotate • Scroll to zoom • Right-click to pan
      </div>
    </div>
  )
}

export default BrainCanvas
